import {
  V2ListDocumentTemplatesForAccountApiArg,
  useV2ListDocumentTemplatesForAccountQuery,
} from "@generated";
import type { DocumentTemplate } from "../types";

export interface UseDocumentTemplatesListArgs {
  page?: V2ListDocumentTemplatesForAccountApiArg["page"];
  filter?: V2ListDocumentTemplatesForAccountApiArg["filter"];
}

export interface DocumentTemplatesListPageMeta {
  recordCount: number;
  pageCount: number;
}

export const useDocumentTemplatesList = ({
  page,
  filter,
}: UseDocumentTemplatesListArgs) => {
  // v2 infers the account from the auth token — no slug needed.
  const { data, error, isLoading, isFetching, refetch } =
    useV2ListDocumentTemplatesForAccountQuery(
      { page, filter },
      // Refetch on remount (e.g. returning from Edit) so saved changes show
      // without a manual reload — the generated API has no cache tags.
      { refetchOnMountOrArgChange: true }
    );

  const rows: DocumentTemplate[] = (data?.data ?? []).map((template) => ({
    id: template.id ?? "",
    name: template.attributes?.name ?? "",
    // `active` is a computed attribute absent from the generated v2 type.
    active: (template.attributes as { active?: boolean })?.active,
    format: template.attributes?.format ?? "word_template",
    filenameFormat: template.attributes?.["filename-format"],
    includeFormatting: template.attributes?.["include-formatting"],
    changeOrderDefault: !!template.attributes?.["change-order-default"],
    mergeTemplateFilename: template.attributes?.["merge-template-filename"],
    // @ts-ignore download link lives under links (v2 key is `downloads`)
    downloadhref: template?.links?.downloads,
    templateFormat: template.attributes?.["template-format"],
    teams:
      template.attributes?.teams?.map((t) => ({
        value: t?.id || "",
        label: t?.name || "",
      })) ?? [],
  }));

  const pageMeta: DocumentTemplatesListPageMeta = {
    recordCount: data?.meta?.["record-count"] ?? 0,
    pageCount: data?.meta?.["page-count"] ?? 0,
  };

  return { rows, pageMeta, error, isLoading, isFetching, refetch };
};

export default useDocumentTemplatesList;
